import React from 'react';
import * as yup from 'yup';
import { CreditsFormProps } from '@screens/players/player.actions/credits/types/credits.form.props';
import { MainDialogContent } from '@components/dialog/main.dialog.content';
import { Button, DialogActions, FormControl, FormHelperText, Input, InputLabel } from '@material-ui/core';
import { useAuth } from '@domain/auth/auth.hook';
import { CancelToken } from 'axios';
import { useFormData } from '@domain/hooks/use.form.data';
import { Loading } from '@components/loading';
import { ErrorDisplay } from '@components/error/error.display';
import { FieldContainer, FieldsArea, LabelIcon } from '@screens/players/player.actions/credits/stats.form.styles';
import { StatIcon } from '@screens/players/stat.icon';
import { CenteredRoot, DescText } from '@styles/common';
import { useGame } from '@domain/game.selection/game.selection.hook';
import { GameType } from '@domain/game.selection/types/game.type';
import { number } from 'yup';

const StatsFormSchema = yup.object({
  cash: yup.number(),
  coins: yup.number(),
  stars: yup.number(),
});

interface StatsFormData {
  pid: number;
  cash?: number;
  coins?: number;
  stars?: number;
  message?: string;
}

export const StatsForm = ({ onFinish, player }: CreditsFormProps) => {
  const { authorizedServices } = useAuth();
  const game = useGame();
  const { pid } = player;

  async function sendStats(data: StatsFormData, cancelToken: CancelToken | undefined) {
    if (!data || (!data.cash && !data.coins && !data.stars)) throw new Error('Insert at least one field');
    await StatsFormSchema.validate(data, { abortEarly: false });
    if (cancelToken) cancelToken.throwIfRequested();

    let cashh: number | null = 0;
    let coinss = 0;
    if (data.coins !== undefined) coinss = data.coins;
    if (data.cash !== undefined) cashh = data.cash;

    if (game.type === GameType.Rex) cashh = null;

    return authorizedServices.post('/admin/update-operations/credits', { ...data, cash: cashh, coins: coinss, cancelToken }).then(onFinish);
  }

  const formData = useFormData({ pid }, sendStats);

  const { data, loading, error, validationErrors, submit, handleChange } = formData;

  // TODO remove this var to use Stars field
  const disableTemporarily = false;

  return (
    <>
      <MainDialogContent>
        <CenteredRoot>
          <Loading loading={loading} />

          <fieldset disabled={loading}>
            <DescText>Insert the stats you want to send</DescText>

            <FieldsArea>
              {game.type === GameType.Praia && (
                <FieldContainer>
                  <FormControl>
                    <InputLabel htmlFor='cash'>
                      <StatIcon small icon='iconcash' /> Cash
                    </InputLabel>
                    <Input id='cash' name='cash' value={data.cash} fullWidth onChange={handleChange} error={!!validationErrors?.cash} />
                    <FormHelperText error>{validationErrors?.cash}</FormHelperText>
                  </FormControl>
                </FieldContainer>
              )}

              <FieldContainer>
                <FormControl>
                  <InputLabel htmlFor='coins'>
                    <StatIcon small icon='iconcoins' /> Coins
                  </InputLabel>
                  <Input id='coins' name='coins' value={data.coins} fullWidth onChange={handleChange} error={!!validationErrors?.coins} />
                  <FormHelperText error>{validationErrors?.coins}</FormHelperText>
                </FormControl>
              </FieldContainer>

              <FieldContainer>
                <FormControl>
                  <InputLabel htmlFor='message'>Message:</InputLabel>
                  <Input id='message' name='message' value={data.message} fullWidth onChange={handleChange} error={!!validationErrors?.message} />
                  <FormHelperText error>{validationErrors?.message}</FormHelperText>
                </FormControl>
              </FieldContainer>

              {disableTemporarily && (
                <FieldContainer>
                  <FormControl>
                    <InputLabel htmlFor='stars'>
                      <LabelIcon>star</LabelIcon> Stars
                    </InputLabel>
                    <Input id='stars' name='stars' value={data.stars} fullWidth onChange={handleChange} error={!!validationErrors?.stars} />
                    <FormHelperText error>{validationErrors?.stars}</FormHelperText>
                  </FormControl>
                </FieldContainer>
              )}
            </FieldsArea>
          </fieldset>

          <ErrorDisplay error={error} />
        </CenteredRoot>
      </MainDialogContent>

      <DialogActions>
        <Button onClick={submit} color='primary' disabled={loading}>
          Send
        </Button>
      </DialogActions>
    </>
  );
};
